import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
  redirect,
} from '@tanstack/react-router';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {ArrowLeft, Loader2} from 'lucide-react';
import {z} from 'zod';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Textarea} from '@/components/ui/textarea';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {Typography} from '@/components/ui/typography';
import {toastManager} from '@/components/ui/toast';
import {EventFilterBuilder} from '@/components/eventFilter/eventFilterBuilder';
import {EventFilterMatchCount} from '@/components/eventFilter/eventFilterMatchCount';
import {
  EventFilterPreviewSidebar,
  MobilePreviewDrawer,
} from '@/components/eventFilter/eventFilterPreviewSidebar';
import {
  EMPTY_EVENT_FILTER_DRAFT,
  eventFilterDraftToFilter,
  type EventFilterDraft,
} from '@/apiTypes/eventFilterDraft';
import type {Event as EventApi} from '@/apiTypes/event';
import {MobileEventDetailCard} from '@/components/mobileSearch/mobileEventDetailCard';
import {toMapEvent} from '@/utils/toMapEvent';
import {createSmartList, previewSmartList} from '@/server/smartList';
import {searchEventTags, getSystemTags} from '@/server/eventTags';
import {searchOrganizers} from '@/server/organizer';

const PREVIEW_CAP = 50;
const PREVIEW_DEBOUNCE_MS = 350;

const NewListSearch = z.object({
  kind: z.enum(['smart']).default('smart'),
});

export const Route = createFileRoute('/dashboard/lists/new')({
  validateSearch: NewListSearch,
  beforeLoad: async ({search, context}) => {
    if (search.kind === 'smart') {
      if (!context.featureFlags['advanced-filters']) {
        throw redirect({to: '/dashboard/lists'});
      }
    }
  },
  component: NewListPage,
});

const visibilityOptions = [
  {value: 'PRIVATE' as const, label: 'Private'},
  {value: 'PUBLIC' as const, label: 'Public'},
];

type PreviewState =
  | {kind: 'idle'}
  | {kind: 'loading'}
  | {kind: 'loaded'; events: EventApi[]; totalCount: number}
  | {kind: 'error'; message: string};

/** Tiny inline debounce — no project hook exists yet and this is the
 * only caller. Promote to a shared hook when a second consumer arrives. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function NewListPage() {
  const router = useRouter();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<EventFilterDraft>(
    EMPTY_EVENT_FILTER_DRAFT
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'PRIVATE' | 'PUBLIC'>('PRIVATE');
  const [previewState, setPreviewState] = useState<PreviewState>({
    kind: 'idle',
  });
  const [saving, setSaving] = useState(false);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [systemTags, setSystemTags] = useState<
    Array<{id: string; name: string}>
  >([]);

  useEffect(() => {
    getSystemTags().then(setSystemTags);
  }, []);

  const debouncedDraft = useDebouncedValue(draft, PREVIEW_DEBOUNCE_MS);
  const filter = useMemo(
    () => eventFilterDraftToFilter(debouncedDraft),
    [debouncedDraft]
  );

  useEffect(() => {
    if (!filter) {
      setPreviewState({kind: 'idle'});
      return;
    }
    let cancelled = false;
    setPreviewState({kind: 'loading'});
    previewSmartList({data: {filter}})
      .then((res) => {
        if (!cancelled) {
          setPreviewState({
            kind: 'loaded',
            events: res.events,
            totalCount: res.totalCount,
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const searchTags = useCallback(
    (query: string) => searchEventTags({data: {query}}),
    []
  );

  const handleSearchOrganizers = useCallback(
    (query: string) =>
      searchOrganizers({data: {query}}).then((results) =>
        results.map((o) => ({id: o.id, name: o.name}))
      ),
    []
  );

  const canSave = name.trim().length > 0 && filter !== null && !saving;

  async function save() {
    if (!canSave || !filter) return;
    setSaving(true);
    try {
      const list = await createSmartList({
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          visibility,
          filter,
        },
      });
      toastManager.add({title: 'Smart list created', type: 'success'});
      await router.invalidate();
      navigate({to: '/dashboard/lists'});
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to create list',
        type: 'error',
      });
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    save();
  }


  const renderEventCard = (event: EventApi) => (
    <MobileEventDetailCard key={event.id} event={toMapEvent(event)} />
  );

  return (
    <div className="flex flex-col gap-6 h-full">
      <div className="flex flex-col gap-1">
        <Link
          to="/dashboard/lists"
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Back
        </Link>
        <Typography level="H3">New Smart List</Typography>
      </div>

      <div className="flex flex-col h-full lg:flex-row lg:gap-8">
        <main className="flex-1 min-w-0 pb-24 lg:pb-0">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="smart-list-name">Name *</Label>
              <Input
                id="smart-list-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="smart-list-description">Description</Label>
              <Textarea
                id="smart-list-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your smart list..."
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Visibility</Label>
              <Select
                value={visibility}
                onValueChange={(value) => {
                  if (value === 'PRIVATE' || value === 'PUBLIC') {
                    setVisibility(value);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue>
                    {
                      visibilityOptions.find((o) => o.value === visibility)
                        ?.label
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectPopup>
                  {visibilityOptions.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Typography level="H5">Filters</Typography>
              <EventFilterBuilder
                draft={draft}
                onChange={setDraft}
                searchTags={searchTags}
                systemTags={systemTags}
                searchOrganizers={handleSearchOrganizers}
              />
              <EventFilterMatchCount
                state={
                  previewState.kind === 'idle'
                    ? {kind: 'idle'}
                    : previewState.kind === 'loading'
                      ? {kind: 'loading'}
                      : previewState.kind === 'error'
                        ? {kind: 'error'}
                        : {kind: 'loaded', totalCount: previewState.totalCount}
                }
                onPreview={() => setPreviewDrawerOpen(true)}
              />
            </div>

            <div className="hidden lg:flex justify-end gap-2 pt-2">
              <Link to="/dashboard/lists">
                <Button variant="outline" type="button">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={!canSave}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Saving...' : 'Save Smart List'}
              </Button>
            </div>
          </form>
        </main>

        <EventFilterPreviewSidebar
          state={previewState}
          cap={PREVIEW_CAP}
          renderEvent={renderEventCard}
        />

        <MobilePreviewDrawer
          open={previewDrawerOpen}
          onOpenChange={setPreviewDrawerOpen}
          state={previewState}
          cap={PREVIEW_CAP}
          renderEvent={renderEventCard}
        />
      </div>

      {/* Mobile fixed Cancel / Save bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-neutral-200 bg-background px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex justify-end gap-2 shadow-lg">
        <Link to="/dashboard/lists">
          <Button variant="outline" type="button">
            Cancel
          </Button>
        </Link>
        <Button type="button" onClick={save} disabled={!canSave}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? 'Saving...' : 'Save Smart List'}
        </Button>
      </div>
    </div>
  );
}
